import { useEffect, useRef } from 'react';
import { useAiChatContextSafe } from 'juneau';

interface Options {
  /** False while the panel is open — an assistant turn is read as it arrives. */
  readonly closed: boolean;
  readonly markUnread: () => void;
}

/**
 * Flags an unread badge when the assistant finishes a turn while the chat is closed.
 * Marks on completion, not on the first token, so a cancelled turn never lights the badge.
 */
export function useUnreadWatcher({ closed, markUnread }: Options): void {
  const chat = useAiChatContextSafe();
  const isLoading = chat?.isLoading ?? false;
  const error = chat?.error ?? null;
  const messageCount = chat?.messages.length ?? 0;

  // Reading these from a ref keeps the effect keyed on the stream lifecycle alone.
  const closedRef = useRef(closed);
  closedRef.current = closed;
  const markRef = useRef(markUnread);
  markRef.current = markUnread;
  const countRef = useRef(messageCount);
  countRef.current = messageCount;

  // True once a stream has actually started, so mount alone never counts as a turn.
  const streamingRef = useRef(false);
  // Message count when the stream began — a turn that added nothing is not news.
  const countAtStartRef = useRef(messageCount);

  useEffect(() => {
    if (isLoading) {
      if (!streamingRef.current) {
        streamingRef.current = true;
        countAtStartRef.current = countRef.current;
      }
      return;
    }

    if (!streamingRef.current) return;
    streamingRef.current = false;

    // A failed turn has nothing to read, and an open panel is already showing it.
    if (error !== null || !closedRef.current) return;
    if (countRef.current <= countAtStartRef.current) return;

    markRef.current();
  }, [isLoading, error]);
}
